// @ts-check

import { mediaQueryLarge } from '@theme/utilities';

/** Inline search enhancements for the Ghaliya header (derived from native header). */
const header = document.querySelector('#header-component.ghaliya-header');

if (header) {
  /** @type {HTMLInputElement | null} */
  const searchInput = header.querySelector('.gh-search__input');
  const wide = searchInput?.getAttribute('data-ph-wide');
  const narrow = searchInput?.getAttribute('data-ph-narrow');

  if (searchInput && narrow && wide && narrow !== wide) {
    const mobilePlaceholderQuery = window.matchMedia('(max-width: 749px)');
    const applyPlaceholder = () => {
      if (searchInput) searchInput.placeholder = mobilePlaceholderQuery.matches ? narrow : wide;
    };
    applyPlaceholder();
    mobilePlaceholderQuery.addEventListener('change', applyPlaceholder);
  }

  /**
   * Centred-logo layout squeezes the menu column; native overflow-list then sets
   * minimum-reached and flips data-menu-style to drawer (hamburger + "More") even
   * on wide viewports. Keep inline nav on desktop; drawer only below 750px.
   */
  const syncGhaliyaMenuMode = () => {
    const overflowList = header.querySelector('overflow-list');

    if (mediaQueryLarge.matches) {
      if (overflowList && 'showAll' in overflowList && typeof overflowList.showAll === 'function') {
        overflowList.showAll();
      } else {
        overflowList?.setAttribute('disabled', 'true');
      }
      overflowList?.removeAttribute('minimum-reached');
      header.dataset.menuStyle = 'menu';
      return;
    }

    overflowList?.removeAttribute('disabled');
    header.dataset.menuStyle = 'drawer';
    overflowList?.dispatchEvent(new CustomEvent('reflow', { bubbles: true, detail: {} }));
  };

  syncGhaliyaMenuMode();
  mediaQueryLarge.addEventListener('change', syncGhaliyaMenuMode);
  window.addEventListener('resize', syncGhaliyaMenuMode, { passive: true });

  header.addEventListener(
    'overflowMinimum',
    () => {
      if (mediaQueryLarge.matches) syncGhaliyaMenuMode();
    },
    true
  );

  const menuStyleObserver = new MutationObserver(() => {
    if (mediaQueryLarge.matches && header.dataset.menuStyle === 'drawer') {
      syncGhaliyaMenuMode();
    }
  });
  menuStyleObserver.observe(header, { attributes: true, attributeFilter: ['data-menu-style'] });

  // overflow-list reflows asynchronously after styles load — re-sync once it settles.
  requestAnimationFrame(() => syncGhaliyaMenuMode());
  setTimeout(syncGhaliyaMenuMode, 150);

  const predictiveSearch = header.querySelector('.gh-predictive-search');
  if (predictiveSearch && searchInput) {
    // Results stay hidden on focus until the visitor types in this session.
    let activeSearch = false;

    const syncResultsPanel = () => {
      const hasQuery = searchInput.value.trim().length > 0;
      predictiveSearch.classList.toggle('gh--has-query', activeSearch && hasQuery);
    };

    searchInput.addEventListener('focus', () => {
      activeSearch = false;
      syncResultsPanel();
    });

    searchInput.addEventListener('input', () => {
      activeSearch = searchInput.value.trim().length > 0;
      syncResultsPanel();
    });

    searchInput.addEventListener('blur', () => {
      activeSearch = false;
      syncResultsPanel();
    });

    syncResultsPanel();

    // Keep the input focused on pointerdown so :focus-within does not drop before
    // the click reaches product links (blur was hiding the panel and swallowing navigation).
    predictiveSearch.querySelector('.gh-predictive-search__results')?.addEventListener('pointerdown', (event) => {
      if (event.target.closest('a, button')) {
        event.preventDefault();
      }
    });

    predictiveSearch.querySelector('.gh-search__reset')?.addEventListener('click', () => {
      activeSearch = false;
      syncResultsPanel();
      setTimeout(() => searchInput.blur(), 150);
    });

    predictiveSearch.addEventListener('keydown', (/** @type {Event & { key?: string }} */ event) => {
      if (event.key === 'Escape') {
        activeSearch = false;
        syncResultsPanel();
        requestAnimationFrame(() => searchInput?.blur());
      }
    });
  }
}
